import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import isEqual from 'lodash/isEqual';
import { createSelector } from 'reselect';
import { tableHelper } from '@builttocreate/engine-utils';

import FieldTitle from '../FieldTitle';
import TextField from '../TextField';
import ImageField from '../ImageField';
import DropdownGenericField from '../DropdownGenericField';

import FieldTableColumnTypes from '../../../../constants/FieldTableColumnTypes';
import FieldTypes from '../../../../constants/FieldTypes';

import computeStyleChanges from '../../../../lib/computeStyleChanges';

const { getCellOperand } = tableHelper;

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledSelect = styled.select`
  width: 16px;
  height: 16px; 
  margin-right: 6px;
  background-color: transparent;
  color: ${(props) => props.fontColor};
  border: 1px solid ${(props) => props.borderColor};
  border-radius: ${(props) => props.borderRadius}px;
  margin-top: -4px !important;
  margin-right: 4px !important;
  transition: all 0.15s ease-in-out;

  &:hover {
    cursor: pointer;
    outline: unset;
    border: 1px solid ${(props) => props.primaryColor};
  }

  &:focus, &:focus-visible {
    outline: unset;
    border: 1px solid ${(props) => props.borderColor};
  }

`;

const StyledTitleWrapper = styled.div`
  padding-top: 4px;
  display: flex;
  flex-direction: row;
  align-items: center;
  svg{
    margin-right:15px;
  }
`;

const StyledTitle = styled.div`
  flex: 1;
  margin-bottom:4px;
`;

const StyledRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  flex-wrap: wrap;
  background-color: ${(props) => props.backgroundColor};
  border: ${(props) => props.border};
  border-radius: ${(props) => props.borderRadius}px;
  padding: ${(props) => props.padding}px;
`;

const StyledFieldWrapper = styled.div`
  ${(props) => {

    if (!props.width) return 'flex: 1;';

    return `
      max-width: ${props.width}px;
      min-width: ${props.width}px;
    `;

  }}
  display: flex;
  align-items: flex-end;
  padding: ${(props) => (props.padding !== undefined ? `${props.padding}px` : '3px')};
`;

class InputGroupFieldRow extends Component {

  static propTypes = {

    id: PropTypes.string,
    title: PropTypes.string,
    row: PropTypes.object,
    rowIndex: PropTypes.number,
    columns: PropTypes.array,
    tableLookup: PropTypes.object,
    focused: PropTypes.string,
    disabled: PropTypes.bool,

    primaryColor: PropTypes.string,
    dangerColor: PropTypes.string,

    //Title styles
    titleDisplay: PropTypes.string,
    titleFontSize: PropTypes.number,
    titleFontColor: PropTypes.string,
    titleTextAlign: PropTypes.string,
    titleFontStyle: PropTypes.string,
    titleFontWeight: PropTypes.string,
    titleTextTransform: PropTypes.string,
    titleTextDecoration: PropTypes.string,

    //Column title styles
    columnTitleFontSize: PropTypes.number,
    columnTitleFontColor: PropTypes.string,
    columnTitleTextAlign: PropTypes.string,
    columnTitleFontStyle: PropTypes.string,
    columnTitleFontWeight: PropTypes.string,
    columnTitleTextTransform: PropTypes.string,
    columnTitleTextDecoration: PropTypes.string,
    columnTitleBackgroundColor: PropTypes.string,
    columnTitlePadding: PropTypes.number,

    //Styles
    fontSize: PropTypes.number,
    fontColor: PropTypes.string,
    fontStyle: PropTypes.string,
    fontWeight: PropTypes.string,
    textAlign: PropTypes.string,
    textTransform: PropTypes.string,
    textDecoration: PropTypes.string,
    textOverflow: PropTypes.string,
    borderRadius: PropTypes.number,
    borderColor: PropTypes.string,
    borderWidth: PropTypes.number,
    backgroundColor: PropTypes.string,
    padding: PropTypes.number,

    //Action handlers
    onCellChange: PropTypes.func,
    onCellBlur: PropTypes.func,
    onCellFocus: PropTypes.func,
    onRowDelete: PropTypes.func,
    onRowDuplicate: PropTypes.func,
    onRowMoveUp: PropTypes.func,
    onRowMoveDown: PropTypes.func,
    onRowAddBelow: PropTypes.func,
    onLoadCompleted: PropTypes.func,
  }

  shouldComponentUpdate(nextProps) {
    return shouldComponentUpdate(nextProps, this.props);
  }

  componentDidMount() {

    const { rowIndex, columns, onLoadCompleted } = this.props;

    /**
     * If this row has no image columns then immediately fire onLoadCompleted
     * because we don't have to wait for any images to load.
     */
    const imageColumns = getTableImageColumns({ columns });
    if (onLoadCompleted && imageColumns.length < 1) onLoadCompleted(rowIndex);

  }

  render() {

    const {
      id,
      title,

      row,
      rowIndex,
      tableLookup,
      columns,
      focused,
      disabled,

      primaryColor,
      dangerColor,

      //Title styles
      titleDisplay,
      titleFontSize,
      titleFontColor,
      titleTextAlign,
      titleFontStyle,
      titleFontWeight,
      titleTextTransform,
      titleTextDecoration,

      //Column title styles
      columnTitleFontSize,
      columnTitleFontColor,
      columnTitleTextAlign,
      columnTitleFontStyle,
      columnTitleFontWeight,
      columnTitleTextTransform,
      columnTitleTextDecoration,
      columnTitleBackgroundColor,
      columnTitlePadding,

      //Field style
      fontSize,
      fontColor,
      fontStyle,
      fontWeight,
      textAlign,
      textTransform,
      textDecoration,
      textOverflow,
      borderRadius,
      borderColor,
      borderWidth,
      backgroundColor,
      padding,

      //Action handlers
      onCellChange,
      onCellFocus,
      onCellBlur,
      onCellUpload,
      onLoadCompleted,
    } = this.props;

    const styles = {
      primaryColor,
      dangerColor,

      //Title styles
      titleFontSize: columnTitleFontSize,
      titleFontColor: columnTitleFontColor,
      titleTextAlign: columnTitleTextAlign,
      titleFontStyle: columnTitleFontStyle,
      titleFontWeight: columnTitleFontWeight,
      titleTextTransform: columnTitleTextTransform,
      titleTextDecoration: columnTitleTextDecoration,
      //Field Styles
      fontSize,
      fontColor,
      fontStyle,
      fontWeight,
      textAlign,
      textTransform,
      textDecoration,
      textOverflow,
      borderColor,
      borderWidth,
      borderRadius,
      backgroundColor,
      padding,
    };

    /**
     * We join width and color to a single border style because chrome displays
     * the border as a two tone color if you use border-color and border-width
     * indvidually.
     */
    const border = `${borderWidth}px solid ${borderColor}`;

    const cells = columns.map((column, columnIndex) => {

      const rowId = row._id;
      const cell = row.cells && row.cells[column._id] ? row.cells[column._id] : null;
      const cellKey = `${row._id}_${rowIndex}_${column._id}`;
      const cellOperand = getCellOperand(column.operand, rowIndex + 1);
      const isFocused = focused === `${row._id}_${column._id}`;

      const fieldWrapperStyles = { width: column.width, padding: columnTitlePadding };

      const testId = `${id}-${rowId}-${column._id}-${columnIndex}`;

      if (column.type === FieldTableColumnTypes.dropdown) {
        return (
          <StyledFieldWrapper
            key={cellKey}
            data-testid={`${testId}-input-group-field-field-wrapper`}
            {...fieldWrapperStyles}
          >
            <DropdownGenericField
              id={testId}
              fieldId={column._id}
              type="text"
              title={column.title}
              disabled={disabled}
              focused={isFocused}
              value={cell || ''}
              options={column.options}
              {...styles}
              onFocus={(fieldId, fieldIdentifier, fieldPositionId, e) => {
                if (onCellFocus) onCellFocus(rowId, column._id, column.identifier, e);
              }}
              onBlur={(fieldId, fieldIdentifier, fieldPositionId, e) => {

                if (onCellBlur) onCellBlur(rowId, column._id, column.identifier, e);

              }}
              onChange={(f, i, p, value) => { if (onCellChange) onCellChange(rowId, column._id, value); }}
            />
          </StyledFieldWrapper>
        );
      } else if (column.type === FieldTableColumnTypes.image) {
        return (
          <StyledFieldWrapper
            key={cellKey}
            data-testid={`${testId}-input-group-field-field-wrapper`}
            {...fieldWrapperStyles}
          >
            <ImageField
              id={testId}
              type={FieldTypes.image} // Type is needed to only allow images for image field
              multi
              disabled={disabled}
              value={cell}
              fieldId={column._id}
              title={column.title}
              primaryMaxWidth={column.maxImageWidth}
              primaryMaxHeight={column.maxImageHeight}
              onUpload={(f, i, p, value) => { if (onCellUpload) onCellUpload(rowId, column._id, value); }}
              onChange={(f, i, p, value) => { if (onCellChange) onCellChange(rowId, column._id, value); }}
              onFocus={() => {
                if (onCellFocus) onCellFocus(rowId, column._id, column.identifier, {});
              }}
              onBlur={(e) => {
                if (onCellBlur) onCellBlur(rowId, column._id, column.identifier, e);
              }}
              {...styles}
              padding={2}
              measure={!!onLoadCompleted}
              onMeasureCompleted={() => this.handleLoadCompleted(column._id)}
            />
          </StyledFieldWrapper>
        );
      } else {
        /**
         * <------------------------NOTICE---------------->
         * We are passing type to ensure an empty string is set as value inside text field
         */
        return (
          <StyledFieldWrapper
            key={cellKey}
            data-testid={`${testId}-input-group-field-field-wrapper`}
            {...fieldWrapperStyles}
          >
            <TextField
              id={testId}
              type={column.type}
              fieldId={column._id}
              title={column.title}
              disabled={disabled}
              focused={isFocused}
              value={isFocused ? cell : tableLookup[cellOperand]}
              wrapperStyles={{ height: 'auto' }}
              {...styles}
              onFocus={(fieldId, fieldIdentifier, fieldPositionId, e) => {
                if (onCellFocus) onCellFocus(rowId, column._id, column.identifier, e);
              }}
              onBlur={(fieldId, fieldIdentifier, fieldPositionId, e) => {
                if (onCellBlur) onCellBlur(rowId, column._id, column.identifier, e);
              }}
              onChange={(f, i, p, value) => { if (onCellChange) onCellChange(rowId, column._id, value); }}
            />
          </StyledFieldWrapper>
        );
      }

    });

    return (
      <StyledWrapper>
        <StyledTitleWrapper>
          {disabled ? null : (
            <StyledSelect
              {...{
                fontColor,
                borderColor,
                borderRadius,
                primaryColor
              }}
              value="cog"
              disabled={disabled}
              onChange={this.handleMenuSelection}
              data-testid={`${id}-${row._id}-input-group-field-row-menu`}
            >
              <option value="cog" />
              <option value="addBelow">Insert Below</option>
              <option value="duplicate">Duplicate</option>
              <option value="moveUp">Move Up</option>
              <option value="moveDown">Move Down</option>
              <option value="delete">Delete</option>
            </StyledSelect>
          )}
          <StyledTitle>
            <FieldTitle
              {...{
                title,
                titleDisplay,
                titleFontSize,
                titleFontColor,
                titleTextAlign,
                titleFontStyle,
                titleFontWeight,
                titleTextTransform,
                titleTextDecoration,
              }}
            />
          </StyledTitle>

        </StyledTitleWrapper>
        <StyledRow
          key={row._id}
          data-testid={`${id}-${row._id}-${rowIndex}-input-group-field-row`}
          {...{
            backgroundColor: columnTitleBackgroundColor,
            padding: columnTitlePadding,
            border,
            borderRadius,
          }}
        >
          {cells}
        </StyledRow>
      </StyledWrapper>
    );

  }

  handleLoadCompleted = (columnId) => {

    const { onLoadCompleted, columns, rowIndex } = this.props;

    const imageColumns = getTableImageColumns({ columns });

    if (!this.loaded) this.loaded = {};
    this.loaded[columnId] = true;

    /**
     * Only update measurements once all fields have been measured.
     */
    if (onLoadCompleted && Object.keys(this.loaded).length === imageColumns.length) onLoadCompleted(rowIndex);

  }

  handleMenuSelection = (e) => {

    const {
      row,
      rowIndex,
      onRowAddBelow,
      onRowDuplicate,
      onRowMoveUp,
      onRowMoveDown,
      onRowDelete
    } = this.props;

    const rowId = row._id;
    const option = e.target.value;

    if (onRowAddBelow && option === 'addBelow') onRowAddBelow(rowId, rowIndex);
    if (onRowDuplicate && option === 'duplicate') onRowDuplicate(rowId, rowIndex);
    if (onRowMoveUp && option === 'moveUp') onRowMoveUp(rowId, rowIndex);
    if (onRowMoveDown && option === 'moveDown') onRowMoveDown(rowId, rowIndex);
    if (onRowDelete && option === 'delete') onRowDelete(rowId, rowIndex);

  }

}

export const getTableImageColumns = createSelector(
  (state) => state.columns,
  (columns) => {

    /**
     * Return all image columns that exist inside the tableColumnOrder
     */
    return columns.filter((column) => column.type === FieldTableColumnTypes.image);

  }
);

const shouldComponentUpdate = (nextProps, props) => {

  const { focused, columns, row, tableLookup, disabled, title } = props;

  /**
   * Row is impacted if either the previously editing cell belonged to this
   * row or the next editing cell belongs to this row.
   */
  const editingCellImpactsRow = (focused && focused.indexOf(row._id) !== -1)
    || (nextProps.focused && nextProps.focused.indexOf(row._id) !== -1);

  const stylesChanged = computeStyleChanges(nextProps, props);

  /**
   * Only pass table lookup for each row.
   */
  if (
    (focused !== nextProps.focused && editingCellImpactsRow)
    || disabled !== nextProps.disabled
    || !isEqual(columns, nextProps.columns)
    || !isEqual(tableLookup, nextProps.tableLookup)
    || !isEqual(row, nextProps.row)
    || stylesChanged
    || title !== nextProps.title
  ) {
    return true;
  } else {
    return false;
  }

};

export { shouldComponentUpdate };

export default InputGroupFieldRow;
